// SNIGJ - Payment Success Page
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, XCircle, ArrowLeft, Receipt, Loader2 } from 'lucide-react';
import api from '../lib/api';

const PaymentSuccessPage = () => {
  const { language } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 5;

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      setTransaction(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setStatus('pending');
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-20 h-20 text-green-500" />;
      case 'pending':
      case 'checking':
        return <Loader2 className="w-20 h-20 text-yellow-500 animate-spin" />;
      case 'expired':
      case 'error':
      case 'timeout':
        return <XCircle className="w-20 h-20 text-red-500" />;
      default:
        return <Clock className="w-20 h-20 text-gray-500" />;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'success':
        return language === 'fr' ? 'Paiement réussi !' : 'Payment Successful!';
      case 'pending':
      case 'checking':
        return language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...';
      case 'expired':
        return language === 'fr' ? 'Session expirée' : 'Session Expired';
      case 'error':
        return language === 'fr' ? 'Erreur de vérification' : 'Verification Error';
      case 'timeout':
        return language === 'fr' ? 'Délai d\'attente dépassé' : 'Verification Timeout';
      default:
        return '';
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto mt-10" data-testid="payment-success-page">
        <Card className="text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              {getStatusIcon()}
            </div>
            <CardTitle className="text-2xl">
              {getStatusMessage()}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {status === 'success' && transaction && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-left">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'fr' ? 'Transaction' : 'Transaction'}:
                    </span>
                    <span className="font-mono text-sm">{transaction.transaction_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'fr' ? 'Montant' : 'Amount'}:
                    </span>
                    <span className="font-bold">${transaction.amount?.toFixed(2)} {transaction.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === 'fr' ? 'Description' : 'Description'}:
                    </span>
                    <span>{transaction.description}</span>
                  </div>
                </div>
              </div>
            )}

            {(status === 'pending' || status === 'checking') && (
              <p className="text-muted-foreground">
                {language === 'fr' 
                  ? 'Veuillez patienter pendant que nous confirmons votre paiement...'
                  : 'Please wait while we confirm your payment...'}
              </p>
            )}

            {(status === 'error' || status === 'timeout') && (
              <p className="text-muted-foreground">
                {language === 'fr'
                  ? 'Nous n\'avons pas pu confirmer votre paiement. Veuillez vérifier votre email pour la confirmation.'
                  : 'We couldn\'t confirm your payment. Please check your email for confirmation.'}
              </p>
            )}

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/citizen')} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                {language === 'fr' ? 'Retour au portail' : 'Back to Portal'}
              </Button>
              {status === 'success' && (
                <Button onClick={() => navigate('/citizen?tab=fees')} className="gap-2">
                  <Receipt className="w-4 h-4" />
                  {language === 'fr' ? 'Voir mes frais' : 'View My Fees'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PaymentSuccessPage;
